import React, { FC, useMemo } from 'react';

import { ProductData } from '@appcharge/shared-ui';
import { EmblemType, Offer, RibbonType } from 'common/contracts';
import { resolveProgressBarPoints } from 'views/Offers/utils/resolveProgressBarPoints';

import { DailyBonusPreview } from './components/DailyBonusPreview';
import { StandardPopupPreview } from './components/StandardPopupPreview';
import { TriggeredPopupPreview } from './components/TriggeredPopupPreview';
import { mapToPreviewProducts } from './PreviewSection.utils';

interface IProgressBarFormValues {
  progressBarId: string | null;
  progressBarPoints: number | null;
}

interface IPreviewSectionProps {
  publisherId?: string;
  currentOfferDesign: string;
  isDailyBonus: boolean;
  isTriggeredPopup?: boolean;
  currentPrice: number | null;
  products: any;
  productsList: ProductData[];
  progressBarFormValues?: IProgressBarFormValues;
  progressBarOffers?: Offer[];
  emblemBadges?: EmblemType[];
  ribbonBadges?: RibbonType[];
}

const PreviewSection: FC<IPreviewSectionProps> = ({
  publisherId,
  currentOfferDesign,
  isDailyBonus,
  isTriggeredPopup = false,
  currentPrice,
  products,
  productsList,
  progressBarFormValues,
  progressBarOffers,
  emblemBadges,
  ribbonBadges
}): React.ReactElement => {
  const previewProducts = useMemo<ProductData[]>(
    () => mapToPreviewProducts(products, productsList),
    [products, productsList]
  );

  const progressBarPoints = useMemo(
    () =>
      resolveProgressBarPoints({
        progressBarId: progressBarFormValues?.progressBarId,
        points: progressBarFormValues?.progressBarPoints,
        progressBarOffers: progressBarOffers ?? []
      }),
    [progressBarFormValues, progressBarOffers]
  );

  if (isTriggeredPopup) {
    return (
      <TriggeredPopupPreview
        publisherId={publisherId}
        currentOfferDesign={currentOfferDesign}
        currentPrice={currentPrice}
        previewProducts={previewProducts}
        progressBarPoints={progressBarPoints}
        emblemBadges={emblemBadges}
        ribbonBadges={ribbonBadges}
      />
    );
  }

  if (isDailyBonus) {
    return (
      <DailyBonusPreview
        publisherId={publisherId}
        currentOfferDesign={currentOfferDesign}
        previewProducts={previewProducts}
        progressBarPoints={progressBarPoints}
      />
    );
  }

  return (
    <StandardPopupPreview
      publisherId={publisherId}
      currentOfferDesign={currentOfferDesign}
      currentPrice={currentPrice}
      previewProducts={previewProducts}
      progressBarPoints={progressBarPoints}
    />
  );
};

export { PreviewSection };
export default PreviewSection;
